import { Instagram } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { CommentAutomationTab } from '@/components/comments/CommentAutomationTab'
import { CommentsTab } from '@/components/comments/CommentsTab'
import { InstagramInsightsTab } from '@/components/instagram/InstagramInsightsTab'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { PageHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useTranslations } from '@/hooks/use-translations'
import { useInstagramStatusQuery } from '@/queries/meta'

/**
 * Comments, comment-to-DM automation and insights of the connected Instagram
 * account. It is also the screen recorded for Meta's review of
 * `instagram_business_manage_comments` and `instagram_business_manage_insights`.
 */
export function InstagramPage() {
  const { t } = useTranslations()
  const { lang } = useParams<{ lang: string }>()
  const status = useInstagramStatusQuery()
  const channelsHref = `/${lang}/settings?tab=channels`
  const username = status.data?.metadata?.instagram_username

  return (
    <div className="container mx-auto space-y-8 py-6">
      <PageHeader
        title={t('instagram_page.title')}
        description={status.data?.connected && username
          ? t('instagram_page.connected_as', { username })
          : t('instagram_page.description')}
      >
        {status.data?.connected && (
          <Button asChild variant="outline" size="sm">
            <Link to={channelsHref}>{t('instagram_page.manage_connection')}</Link>
          </Button>
        )}
      </PageHeader>

      {status.isLoading && <Skeleton className="h-64 w-full" />}

      {!status.isLoading && !status.data?.connected && (
        <EmptyState
          icon={<Instagram className="h-6 w-6" />}
          title={t('instagram_page.not_connected_title')}
          description={t('instagram_page.not_connected_description')}
        >
          <Button asChild>
            <Link to={channelsHref}>{t('instagram_page.connect')}</Link>
          </Button>
        </EmptyState>
      )}

      {status.data?.connected && (
        <Tabs defaultValue="comments" className="space-y-8">
          <TabsList>
            <TabsTrigger value="comments">{t('instagram_page.tabs.comments')}</TabsTrigger>
            <TabsTrigger value="automation">{t('instagram_page.tabs.automation')}</TabsTrigger>
            <TabsTrigger value="insights">{t('instagram_page.tabs.insights')}</TabsTrigger>
          </TabsList>
          <TabsContent value="comments"><CommentsTab channel="instagram" /></TabsContent>
          <TabsContent value="automation"><CommentAutomationTab channel="instagram" /></TabsContent>
          <TabsContent value="insights"><InstagramInsightsTab /></TabsContent>
        </Tabs>
      )}
    </div>
  )
}
